'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

interface ProjectsFormValues {
  kalpTaruhTrees: string;
  kalpTaruhDate: string;
  kalpTaruhNotes: string;
  pvChampion1Name: string;
  pvChampion1Habits: string;
  pvChampion2Name: string;
  pvChampion2Habits: string;
  pvChampion3Name: string;
  pvChampion3Habits: string;
  pvAwardDate: string;
  pvParticipants: string;
  pvNotes: string;
  gokulVillageName: string;
  gokulComposting: string;
  gokulWaterHarvesting: string;
  gokulNoBurning: string;
  gokulKitchenGardens: string;
  gokulMGNREGS: string;
  gokulNotes: string;
}

interface ProjectsTabProps {
  onChange: () => void;
  onSnapshot?: (key: string, value: string) => void;
}

const sectionClass = 'bg-card rounded-2xl border border-border shadow-card p-5 space-y-4';
const projectHeaderClass = 'flex items-center gap-3 pb-3 border-b border-border';

const habitOptions = [
  'Zero Plastic Use',
  'Kitchen Garden',
  'Composting',
  'Carpool / Car Share',
  'LED Lighting',
  'Water Conservation',
  'Vegetarian Diet',
  'Repair & Reuse',
  'Plantable Paper',
  'Organic Food',
  'Waste Segregation',
  'Shorter Showers',
];

export default function ProjectsTab({ onChange, onSnapshot }: ProjectsTabProps) {
  const [kalpExpanded, setKalpExpanded] = useState(true);
  const [pvExpanded, setPvExpanded] = useState(true);
  const [gokulExpanded, setGokulExpanded] = useState(true);

  const {
    register,
    formState: { errors },
  } = useForm<ProjectsFormValues>({
    defaultValues: {
      kalpTaruhTrees: '',
      pvChampion1Name: '',
      pvChampion2Name: '',
      pvChampion3Name: '',
      gokulComposting: '0',
      gokulWaterHarvesting: '0',
      gokulNoBurning: '0',
      gokulKitchenGardens: '0',
    },
  });

  const snap = (key: string, value: string) => {
    onChange();
    onSnapshot?.(key, value);
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-xl border border-primary/20">
        <Icon name="InformationCircleIcon" size={16} variant="outline" className="text-primary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary font-medium">
          Report your centre's project activities for the selected month. Kalp Taruh data is cross-referenced with the Kalp Taruh mobile app.
        </p>
      </div>

      {/* 1. Kalp Taruh */}
      <div className={sectionClass}>
        <div className={projectHeaderClass}>
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="GlobeAltIcon" size={18} variant="outline" className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">1. Kalp Taruh — Tree Plantation Program 🌳</h3>
            <p className="text-xs text-muted-foreground mt-0.5">1 Person · 1 Plant · 1 Planet</p>
          </div>
          <button
            type="button"
            onClick={() => setKalpExpanded(!kalpExpanded)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label={kalpExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon name={kalpExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} variant="outline" className="text-muted-foreground" />
          </button>
        </div>

        {kalpExpanded && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="kalp-trees">
                  Trees Planted / Registered This Month
                  <span className="text-[var(--danger)] ml-0.5">*</span>
                </label>
                <p className="form-helper">Count of new saplings registered on the Kalp Taruh app</p>
                <input
                  id="kalp-trees"
                  type="number"
                  min="0"
                  placeholder="e.g. 120"
                  className={`form-input mt-1 ${errors.kalpTaruhTrees ? 'form-input-error' : ''}`}
                  {...register('kalpTaruhTrees', {
                    required: 'Tree count is required',
                    min: { value: 0, message: 'Cannot be negative' },
                  })}
                  onChange={(e) => snap('kalpTaruhTrees', e.target.value)}
                />
                {errors.kalpTaruhTrees && <p className="form-error">{errors.kalpTaruhTrees.message}</p>}
              </div>
              <div>
                <label className="form-label" htmlFor="kalp-date">Date of Main Plantation Event</label>
                <input
                  id="kalp-date"
                  type="date"
                  className="form-input mt-1"
                  {...register('kalpTaruhDate')}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="p-3 bg-[var(--success-bg)] rounded-xl border border-[var(--success)]/20">
              <div className="flex items-start gap-2">
                <Icon name="CheckCircleIcon" size={14} variant="outline" className="text-[var(--success)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-[var(--success)]">Kalp Taruh App Integration</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Detailed plant registration, 75-day care tips, and spiritual value connections are managed in the Kalp Taruh mobile app. Only total count is reported here.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="form-label" htmlFor="kalp-notes">Additional Notes</label>
              <textarea
                id="kalp-notes"
                rows={2}
                placeholder="Special occasions, community events, notable participants..."
                className="form-input resize-none"
                {...register('kalpTaruhNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* 2. Prakriti Veer Award */}
      <div className={sectionClass}>
        <div className={projectHeaderClass}>
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <Icon name="TrophyIcon" size={18} variant="outline" className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">3. Prakriti Veer Award</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Identify and award top 3 Environmental Champions at your centre</p>
          </div>
          <button
            type="button"
            onClick={() => setPvExpanded(!pvExpanded)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label={pvExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon name={pvExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} variant="outline" className="text-muted-foreground" />
          </button>
        </div>

        {pvExpanded && (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="pv-award-date">Award Ceremony Date</label>
                <input
                  id="pv-award-date"
                  type="date"
                  className="form-input"
                  {...register('pvAwardDate')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="pv-participants">
                  Total Participants Observed
                  <span className="text-[var(--danger)] ml-0.5">*</span>
                </label>
                <p className="form-helper">People whose habits were observed over the month</p>
                <input
                  id="pv-participants"
                  type="number"
                  min="0"
                  placeholder="e.g. 45"
                  className={`form-input mt-1 ${errors.pvParticipants ? 'form-input-error' : ''}`}
                  {...register('pvParticipants', {
                    required: 'Participant count is required',
                    min: { value: 1, message: 'At least 1 participant required' },
                  })}
                  onChange={(e) => snap('pvParticipants', e.target.value)}
                />
                {errors.pvParticipants && <p className="form-error">{errors.pvParticipants.message}</p>}
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-xs font-semibold text-amber-700 mb-1">21-Day Habit Rule</p>
              <p className="text-xs text-muted-foreground">
                Observe members for a full month. Award the top 3 based on consistent eco-habits including energy saving, water conservation, waste reduction, simple living, resource sharing, plastic reduction, and plantation.
              </p>
            </div>

            {/* Champion entries */}
            <div className="space-y-4">
              {[
                { rank: 1, nameKey: 'pvChampion1Name' as const, habitsKey: 'pvChampion1Habits' as const, color: 'bg-amber-400', label: '🥇 1st Place Champion' },
                { rank: 2, nameKey: 'pvChampion2Name' as const, habitsKey: 'pvChampion2Habits' as const, color: 'bg-gray-300', label: '🥈 2nd Place Champion' },
                { rank: 3, nameKey: 'pvChampion3Name' as const, habitsKey: 'pvChampion3Habits' as const, color: 'bg-orange-300', label: '🥉 3rd Place Champion' },
              ].map((c) => (
                <div key={`champion-entry-${c.rank}`} className="p-4 rounded-xl border border-border bg-muted/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-6 h-6 rounded-full ${c.color} flex items-center justify-center text-xs font-extrabold text-white`}>
                      {c.rank}
                    </div>
                    <span className="text-sm font-semibold text-foreground">{c.label}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label" htmlFor={`champ-${c.rank}-name`}>
                        Full Name
                        {c.rank === 1 && <span className="text-[var(--danger)] ml-0.5">*</span>}
                      </label>
                      <input
                        id={`champ-${c.rank}-name`}
                        type="text"
                        placeholder="e.g. Sunita Devi Sharma"
                        className={`form-input ${errors[c.nameKey] ? 'form-input-error' : ''}`}
                        {...register(c.nameKey, c.rank === 1 ? { required: '1st place champion name is required' } : {})}
                        onChange={(e) => snap(c.nameKey, e.target.value)}
                      />
                      {errors[c.nameKey] && <p className="form-error">{(errors[c.nameKey] as any)?.message}</p>}
                    </div>
                    <div>
                      <label className="form-label" htmlFor={`champ-${c.rank}-habits`}>Key Habits Demonstrated</label>
                      <input
                        id={`champ-${c.rank}-habits`}
                        type="text"
                        placeholder="e.g. Zero Plastic, Composting, Carpool"
                        className="form-input"
                        list={`habits-list-${c.rank}`}
                        {...register(c.habitsKey)}
                        onChange={onChange}
                      />
                      <datalist id={`habits-list-${c.rank}`}>
                        {habitOptions.map((h) => (
                          <option key={`habit-opt-${c.rank}-${h}`} value={h} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="form-label" htmlFor="pv-notes">Award Ceremony Notes</label>
              <textarea
                id="pv-notes"
                rows={2}
                placeholder="How was the award announced? Any special moments or community response..."
                className="form-input resize-none"
                {...register('pvNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* 3. Gokul Gram Project */}
      <div className={sectionClass}>
        <div className={projectHeaderClass}>
          <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="HomeModernIcon" size={18} variant="outline" className="text-secondary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">4. Gokul Gram Project</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Model village transformation near BK Rural Centres</p>
          </div>
          <button
            type="button"
            onClick={() => setGokulExpanded(!gokulExpanded)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label={gokulExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon name={gokulExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} variant="outline" className="text-muted-foreground" />
          </button>
        </div>

        {gokulExpanded && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="form-label" htmlFor="gokul-village">Village Name</label>
              <p className="form-helper">Name of the model village being supported this month</p>
              <input
                id="gokul-village"
                type="text"
                placeholder="e.g. Rajgurunagar, Pune District"
                className="form-input mt-1"
                {...register('gokulVillageName')}
                onChange={(e) => snap('gokulVillageName', e.target.value)}
              />
            </div>

            <div>
              <p className="form-label mb-2">Village Progress Milestones</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { id: 'gokul-composting', key: 'gokulComposting' as const, label: 'Households Composting', icon: 'ArchiveBoxIcon', placeholder: '0' },
                  { id: 'gokul-water', key: 'gokulWaterHarvesting' as const, label: 'Water Harvesting Structures', icon: 'BeakerIcon', placeholder: '0' },
                  { id: 'gokul-burning', key: 'gokulNoBurning' as const, label: 'Zero Open Burning (households)', icon: 'FireIcon', placeholder: '0' },
                  { id: 'gokul-gardens', key: 'gokulKitchenGardens' as const, label: 'Kitchen Gardens Started', icon: 'GlobeAltIcon', placeholder: '0' },
                ].map((item) => (
                  <div key={`gokul-${item.key}`}>
                    <label className="form-label text-[11px]" htmlFor={item.id}>
                      {item.label}
                    </label>
                    <input
                      id={item.id}
                      type="number"
                      min="0"
                      placeholder={item.placeholder}
                      className="form-input"
                      {...register(item.key, { min: { value: 0, message: 'Cannot be negative' } })}
                      onChange={onChange}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[var(--info-bg)] rounded-xl border border-[var(--info)]/20">
              <div className="flex-1">
                <label className="form-label text-xs mb-0" htmlFor="gokul-mgnregs">
                  Linked with MGNREGS / PM Krishi Sinchayee Yojana
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="gokul-mgnregs"
                  type="checkbox"
                  className="rounded border-border accent-primary w-4 h-4"
                  {...register('gokulMGNREGS')}
                  onChange={onChange}
                />
                <label htmlFor="gokul-mgnregs" className="text-xs font-medium text-foreground cursor-pointer">Yes</label>
              </div>
            </div>

            <div>
              <label className="form-label" htmlFor="gokul-notes">Progress Notes</label>
              <textarea
                id="gokul-notes"
                rows={2}
                placeholder="Key achievements, challenges, government support received..."
                className="form-input resize-none"
                {...register('gokulNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}